import { useState } from "react";
import {
  Stethoscope,
  ScanSearch,
  Syringe,
  Droplets,
  Microscope,
  CircleCheckBig,
  ListChecks,
} from "lucide-react";
import { waLink } from "../../lib/constants";
import useReveal from "../../hooks/useReveal";

const PROCEDIMENTOS = [
  {
    slug: "toracocentese",
    title: "Toracocentese",
    subtitle: "Coleta de Líquido Pleural",
    icon: Syringe,
    color: "#0D47A1",
    bg: "rgba(13, 71, 161, 0.08)",
    tag: "Diagnóstico + Terapêutico",
    description:
      "Toracocentese é a retirada do líquido pleural realizada por meio de uma punção percutânea (através da pele) utilizando uma agulha fina. Normalmente é guiada por Ultrassom para maior segurança. É procedimento diagnóstico e pode ser terapêutico (aliviando sintomas ao remover volume).",
    how: [
      "Paciente sentado, confortavelmente posicionado",
      "Anestesia local no ponto de punção",
      "Punção guiada por Ultrassom (USG)",
      "Retirada do líquido pleural e envio para análise laboratorial",
    ],
    vantagens: [
      "Procedimento minimamente invasivo",
      "Alívio imediato dos sintomas (se volume significativo removido)",
      "Não necessita internamento",
    ],
  },
  {
    slug: "drenagem",
    title: "Drenagem Pleural",
    subtitle: "Esvaziamento da Cavidade Pleural",
    icon: Droplets,
    color: "#00897B",
    bg: "rgba(0, 137, 123, 0.08)",
    tag: "Terapêutico",
    description:
      "Quando o derrame pleural precisa ser completamente removido, principalmente em causas infecciosas ou secundário a um trauma, pode ser indicado o posicionamento de um dreno na cavidade pleural.",
    how: [
      "Anestesia local ou sedação",
      "Posicionamento de dreno ou cateter de baixo calibre guiado por USG",
      "Drenagem controlada do líquido para evitar edema pulmonar de reexpansão",
      "Internamento enquanto o dreno for necessário",
    ],
    vantagens: [
      "Resolução de causas infecciosas",
      "Alívio rápido dos sintomas",
      "Permite monitoramento da produção de líquido",
    ],
    indicacoes: ["Causas infecciosas (empiema pleural, derrame parapneumônico complicado)", "Hemotórax (sangue na cavidade)"],
  },
  {
    slug: "pleuroscopia",
    title: "Pleuroscopia VATS",
    subtitle: "Avaliação Completa da Cavidade Pleural",
    icon: Microscope,
    color: "#6A1B9A",
    bg: "rgba(106, 27, 154, 0.08)",
    tag: "Diagnóstico + Tratamento",
    description:
      "Pleuroscopia VATS (Video-Assisted Thoracoscopic Surgery) é a evolução da drenagem — permite visão direta da cavidade pleural, coleta de material para biópsia e tratamento de complicações (como loculações ou septos).",
    how: [
      "Realizado em centro cirúrgico com anestesia geral",
      "2 pequenas incisões (10mm) entre as costelas",
      "Introdução de câmera de alta definição para inspeção da cavidade pleural",
      "Retirada de todo o líquido pleural e eventuais septos ou aderências",
      "Biópsia de áreas suspeitas",
      "Possibilidade de pleurodese (procedimento para prevenir a recorrência do derrame pleural)",
    ],
    vantagens: [
      "Diagnóstico definitivo (visão direta + biópsia)",
      "Tratamento de complicações no mesmo procedimento",
      "Minimamente invasivo (recuperação mais rápida vs. cirurgia aberta)",
    ],
    indicacoes: [
      "Derrame com causa indefinida após toracocentese",
      "Suspeita de malignidade",
      "Empiema com loculações ou septos",
      "Derrame recorrente necessitando pleurodese",
    ],
  },
];

export default function DiagnosticoTratamento() {
  const { ref, isVisible } = useReveal();
  const [active, setActive] = useState("toracocentese");
  const current = PROCEDIMENTOS.find((p) => p.slug === active);
  const CurrentIcon = current.icon;

  return (
    <section
      id="diagnostico"
      ref={ref}
      className={`py-24 md:py-32 bg-white reveal-section ${isVisible ? "is-visible" : ""}`}
      data-testid="diagnostico-section"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#00897B]/10 text-[#00897B] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Diagnóstico e Tratamento
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-6 leading-tight">
            Como Diagnosticamos e Tratamos Derrame Pleural
          </h2>
          <p className="text-base md:text-lg text-[#212121]/70 leading-relaxed">
            O caminho desde a descoberta do derrame até a resolução é bem estruturado. Aqui explicamos
            cada passo — do diagnóstico inicial ao tratamento definitivo — para você entender
            exatamente o que vai acontecer.
          </p>
        </div>

        {/* Etapas do Diagnóstico */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {[
            {
              n: "01",
              icon: Stethoscope,
              title: "Avaliação Clínica",
              desc:
                "História clínica detalhada (quando começou a falta de ar, febre, dor torácica, tosse, histórico de câncer) e exame físico completo com ausculta pulmonar para suspeita diagnóstica inicial.",
            },
            {
              n: "02",
              icon: ScanSearch,
              title: "Exames de Imagem",
              desc:
                "Radiografia simples (detecta derrames acima de 300ml), Tomografia (define localização, volume e características) e Ultrassonografia (guia para procedimentos de coleta).",
            },
            {
              n: "03",
              icon: Microscope,
              title: "Análise do Líquido Pleural",
              desc:
                "Coleta essencial quando há dúvida diagnóstica. Análise: contagem celular, proteínas, glicose, LDH, ADA, cultura e citologia — pode identificar infecção, tumor ou insuficiência cardíaca.",
            },
          ].map((etapa, idx) => {
            const Icon = etapa.icon;
            return (
              <div
                key={idx}
                data-testid={`etapa-diagnostico-${idx + 1}`}
                className="relative bg-[#F5F5F5] rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "rgba(13, 71, 161, 0.08)" }}
                  >
                    <Icon size={20} style={{ color: "#0D47A1" }} />
                  </div>
                  <span className="font-poppins font-bold text-2xl" style={{ color: "rgba(13, 71, 161, 0.2)" }}>
                    {etapa.n}
                  </span>
                </div>
                <h3 className="font-poppins font-bold text-lg text-[#212121] mb-2">{etapa.title}</h3>
                <p className="text-[#212121]/65 text-sm leading-relaxed">{etapa.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Abas de Procedimentos */}
        <div className="text-center mb-8">
          <h3 className="font-poppins font-bold text-2xl md:text-3xl text-[#212121] mb-3">
            Procedimentos: Toracocentese, Drenagem e Pleuroscopia VATS
          </h3>
          <p className="text-[#212121]/65 max-w-2xl mx-auto">
            Dependendo do volume do derrame, dos sintomas e da suspeita diagnóstica, pode ser indicado
            algum procedimento. O objetivo pode ser confirmar uma suspeita e/ou aliviar sintomas.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {PROCEDIMENTOS.map((p) => (
            <button
              key={p.slug}
              data-testid={`procedimento-tab-${p.slug}`}
              onClick={() => setActive(p.slug)}
              className={`px-5 py-3 rounded-full font-poppins font-semibold text-sm transition-all duration-200 border-2 ${
                active === p.slug
                  ? "bg-[#0D47A1] text-white border-[#0D47A1] shadow-lg"
                  : "bg-white text-[#0D47A1] border-[#0D47A1]/25 hover:border-[#0D47A1]"
              }`}
            >
              {p.title}
            </button>
          ))}
        </div>

        <div
          data-testid={`procedimento-content-${current.slug}`}
          className="bg-gradient-to-br from-[#EEF4FF] to-white border border-[#0D47A1]/10 rounded-3xl p-8 md:p-12 shadow-sm"
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
                style={{ backgroundColor: current.bg }}
              >
                <CurrentIcon size={32} style={{ color: current.color }} />
              </div>
              <span
                className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3"
                style={{ backgroundColor: current.bg, color: current.color }}
              >
                {current.tag}
              </span>
              <h4 className="font-poppins font-bold text-2xl md:text-3xl text-[#212121] mb-2">
                {current.title}
              </h4>
              <p className="text-[#0D47A1] font-semibold mb-4">{current.subtitle}</p>
              <p className="text-[#212121]/75 leading-relaxed text-sm md:text-base">
                {current.description}
              </p>
            </div>

            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <ListChecks size={18} className="text-[#0D47A1]" />
                  <h5 className="font-poppins font-bold text-sm text-[#0D47A1] uppercase tracking-wide">
                    Como funciona
                  </h5>
                </div>
                <ul className="space-y-2">
                  {current.how.map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#212121]/75 leading-relaxed">
                      <span className="text-[#0D47A1] font-bold mt-0.5">•</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <CircleCheckBig size={18} className="text-[#00897B]" />
                  <h5 className="font-poppins font-bold text-sm text-[#00897B] uppercase tracking-wide">
                    Vantagens
                  </h5>
                </div>
                <ul className="space-y-2">
                  {current.vantagens.map((v, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#212121]/75 leading-relaxed">
                      <CircleCheckBig size={14} className="text-[#00897B] mt-1 flex-shrink-0" />
                      <span>{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {current.indicacoes && (
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm md:col-span-2">
                  <div className="flex items-center gap-2 mb-3">
                    <Stethoscope size={18} className="text-[#6A1B9A]" />
                    <h5 className="font-poppins font-bold text-sm text-[#6A1B9A] uppercase tracking-wide">
                      Principais Indicações
                    </h5>
                  </div>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {current.indicacoes.map((ind, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#212121]/75 leading-relaxed">
                        <span className="text-[#6A1B9A] font-bold mt-0.5">•</span>
                        <span>{ind}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="diagnostico-cta-button"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold px-10 py-4 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5 hover:shadow-xl"
          >
            QUAL PROCEDIMENTO É IDEAL PARA MEU CASO? AGENDE UMA AVALIAÇÃO
          </a>
        </div>
      </div>
    </section>
  );
}
